/**
 * Desktop wallpapers.
 *
 * Each built-in is drawn as a tiny SVG — tens of pixels across — and scaled up
 * with nearest-neighbour sampling, so the result is genuinely chunky rather
 * than a smooth image with a filter over it. It also keeps them a few hundred
 * bytes each, with nothing to download.
 */

export type WallpaperId = 'none' | 'hill' | 'custom';

export interface Wallpaper {
  id: WallpaperId;
  label: string;
  description: string;
  /** Data URI, or null when the theme's flat desktop colour should show. */
  uri: string | null;
}

const svg = (body: string, w: number, h: number) =>
  `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" shape-rendering="crispEdges">${body}</svg>`
  )}`;

/**
 * A rolling green hill under a blue sky.
 *
 * An original pixel drawing in the spirit of the Windows XP default, not a
 * copy of it — that wallpaper is a copyrighted photograph. This is built from
 * flat rectangles and a stepped horizon, which is what the era's 256-colour
 * displays would have made of such a scene anyway.
 */
const HILL_W = 64;
const HILL_H = 40;

function hillSvg(): string {
  const parts: string[] = [];

  // Sky, banded from deeper blue at the top to pale at the horizon.
  const skyBands = ['#3a6ea5', '#4479b0', '#5a8cc0', '#78a6d2', '#9cc0e4', '#bcd8f0'];
  const bandHeight = 22 / skyBands.length;
  skyBands.forEach((c, i) => {
    parts.push(
      `<rect x="0" y="${(i * bandHeight).toFixed(2)}" width="${HILL_W}" height="${bandHeight.toFixed(2)}" fill="${c}"/>`
    );
  });

  // Two clouds, drawn as stacked runs of pixels.
  const cloud = (x: number, y: number, scale: number) => {
    const rows: Array<[number, number]> = [
      [2, 5],
      [1, 8],
      [0, 10],
    ];
    rows.forEach(([dx, w], i) => {
      parts.push(
        `<rect x="${x + dx * scale}" y="${y + i * scale}" width="${w * scale}" height="${scale}" fill="#ffffff" opacity="0.9"/>`
      );
    });
  };
  cloud(6, 4, 1);
  cloud(42, 8, 1);

  // The hill: a stepped crest, so the silhouette reads as pixels rather than
  // as a curve that happens to be low resolution.
  const crest = (x: number) => {
    const t = x / HILL_W;
    // A broad rise peaking left of centre, the way the original does.
    const curve = Math.sin(Math.PI * Math.min(1, t * 1.15)) ** 1.6;
    return Math.round(22 - curve * 9);
  };

  for (let x = 0; x < HILL_W; x++) {
    const top = crest(x);
    parts.push(`<rect x="${x}" y="${top}" width="1" height="${HILL_H - top}" fill="#5a9e2f"/>`);
    // A lighter band just under the crest, where sun would catch the grass.
    parts.push(`<rect x="${x}" y="${top}" width="1" height="2" fill="#7cc242"/>`);
    // Shade towards the bottom of the frame.
    parts.push(`<rect x="${x}" y="${HILL_H - 6}" width="1" height="6" fill="#3f7a22"/>`);
  }

  return svg(parts.join(''), HILL_W, HILL_H);
}

export const WALLPAPERS: Wallpaper[] = [
  {
    id: 'none',
    label: 'Solid colour',
    description: 'The theme’s own desktop colour.',
    uri: null,
  },
  {
    id: 'hill',
    label: 'Green Hill',
    description: 'Rolling hill and blue sky, in 64 pixels.',
    uri: hillSvg(),
  },
];

export function getWallpaper(id: WallpaperId | null | undefined): Wallpaper {
  return WALLPAPERS.find((w) => w.id === id) ?? WALLPAPERS[0]!;
}

/** Storage keys for the persisted choice. */
export const WALLPAPER_STORAGE_KEY = 'ogaim.wallpaper';
/** A user-uploaded wallpaper URL, when `wallpaper` is `custom`. */
export const WALLPAPER_CUSTOM_KEY = 'ogaim.wallpaper.custom';
